import type { components } from "@sagot/api-client";

export type GapView = components["schemas"]["GapView"];
export type GapDetail = components["schemas"]["GapDetail"];
export type GapStats = components["schemas"]["GapStats"];
export type GapSort = components["schemas"]["GapSort"];
export type GapIgnoreReason = components["schemas"]["GapIgnoreReason"];

/** Console reason vocabulary (badge + tab), normalized from the engine's raw `reason` values. */
export type GapReasonKey = "no_source" | "outdated" | "low_confidence" | "thumbs_down";
export type GapTab = "unanswered" | "lowConfidence" | "negative" | "resolved";
export type OpenTab = Exclude<GapTab, "resolved">;

export const GAP_TABS: readonly GapTab[] = ["unanswered", "lowConfidence", "negative", "resolved"];
export const GAP_SORTS: readonly GapSort[] = ["most_asked", "most_recent", "oldest"];
export const IGNORE_REASONS: readonly GapIgnoreReason[] = ["out_of_scope", "spam", "always_staff", "other"];
/** Open clusters are loaded in one page (API max) so tab counts and bulk selection are exact. */
export const OPEN_PAGE_LIMIT = 200;
export const RESOLVED_PAGE_LIMIT = 20;
export const PHRASINGS_SHOWN = 3;

const RAW_REASONS: Readonly<Record<string, GapReasonKey>> = {
  no_source: "no_source",
  no_sources: "no_source",
  outdated: "outdated",
  outdated_source: "outdated",
  low_confidence: "low_confidence",
  ungrounded: "low_confidence",
  thumbs_down: "thumbs_down",
  negative_feedback: "thumbs_down",
};

const TAB_OF_REASON: Readonly<Record<GapReasonKey, OpenTab>> = {
  no_source: "unanswered",
  outdated: "unanswered",
  low_confidence: "lowConfidence",
  thumbs_down: "negative",
};

export function reasonKey(raw: string | null): GapReasonKey {
  return (raw ? RAW_REASONS[raw] : undefined) ?? "no_source";
}

export function tabOf(gap: Pick<GapView, "reason">): OpenTab {
  return TAB_OF_REASON[reasonKey(gap.reason)];
}

export function countByTab(gaps: readonly GapView[]): Record<OpenTab, number> {
  const counts: Record<OpenTab, number> = { unanswered: 0, lowConfidence: 0, negative: 0 };
  for (const gap of gaps) counts[tabOf(gap)] += 1;
  return counts;
}

export function inTab(gaps: readonly GapView[], tab: OpenTab): GapView[] {
  return gaps.filter((gap) => tabOf(gap) === tab);
}

/** Phrasings shown as chips plus the "+N more" overflow (other asks of the cluster). */
export function phrasingPreview(gap: Pick<GapView, "example_questions" | "ask_count">, shown = PHRASINGS_SHOWN) {
  const visible = gap.example_questions.slice(0, shown);
  const more = Math.max(gap.ask_count, gap.example_questions.length) - visible.length;
  return { visible, more: Math.max(more, 0) };
}

/** Channel counts, largest first. */
export function channelEntries(counts: Readonly<Record<string, number>>): [string, number][] {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
}

/** Bar heights (0–100 %) for the 7-day ask sparkline; null when there were no asks. */
export function trendHeights(trend: readonly number[]): number[] | null {
  const peak = Math.max(0, ...trend);
  if (peak === 0) return null;
  return trend.map((n) => Math.round((n / peak) * 100));
}

export type ExpiryChoice = "never" | "days30" | "days90" | "custom";
export const EXPIRY_CHOICES: readonly ExpiryChoice[] = ["never", "days30", "days90", "custom"];
const EXPIRY_DAYS: Readonly<Record<"days30" | "days90", number>> = { days30: 30, days90: 90 };

/** `valid_until` (ISO date) for an expiry choice; null = never expires. */
export function expiryDate(choice: ExpiryChoice, custom: string, now = new Date()): string | null {
  if (choice === "never") return null;
  if (choice === "custom") return custom || null;
  const date = new Date(now.getTime() + EXPIRY_DAYS[choice] * 86_400_000);
  return date.toISOString().slice(0, 10);
}
